import React, { useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Loader2, Copy, Phone, MessageSquare, Mail, Zap } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { retentionAPI } from '@/lib/api';

function copy(text) {
  navigator.clipboard?.writeText(text).then(
    () => toast.success('Copied'),
    () => toast.error('Copy failed'),
  );
}

// AI Retention Assistant — generates a brief, suggested outreach and
// next-best-action for one opportunity. Summarizes + recommends only.
export default function RetentionAIAssistant({ open, onClose, opportunity }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const generate = async () => {
    if (!opportunity) return;
    setLoading(true);
    setData(null);
    try {
      const r = await retentionAPI.aiBrief(opportunity.client_id, opportunity);
      setData(r.data);
    } catch {
      toast.error('AI brief failed — try again');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (open && opportunity) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, opportunity?.id]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="retention-ai-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={16} className="text-violet-600" /> AI Retention Brief
          </DialogTitle>
          <DialogDescription>
            {opportunity?.client_name} · {opportunity?.reason}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center">
            <Loader2 size={16} className="animate-spin" /> Generating brief…
          </div>
        ) : !data ? (
          <div className="py-6 text-center">
            <Button onClick={generate} data-testid="ai-generate-btn">
              <Sparkles size={14} className="mr-1.5" /> Generate brief
            </Button>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            {data.next_best_action && (
              <div className="flex items-center gap-2 rounded-lg bg-violet-50 border border-violet-200 px-3 py-2" data-testid="ai-nba">
                <Zap size={15} className="text-violet-600 shrink-0" />
                <span className="font-semibold text-violet-800">Next best action:</span>
                <span className="text-violet-900">{data.next_best_action}</span>
              </div>
            )}
            {data.why_now && (
              <Section title="Why contact now">
                <p className="text-slate-700">{data.why_now}</p>
              </Section>
            )}
            {data.brief && (
              <Section title="Client brief">
                <p className="text-slate-700 whitespace-pre-wrap">{data.brief}</p>
              </Section>
            )}
            {Array.isArray(data.call_points) && data.call_points.length > 0 && (
              <Section title="Call talking points" icon={<Phone size={13} className="text-emerald-600" />}>
                <ul className="list-disc pl-5 space-y-1 text-slate-700">
                  {data.call_points.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </Section>
            )}
            {data.sms && (
              <Section title="Suggested SMS" icon={<MessageSquare size={13} className="text-sky-600" />}
                action={<CopyBtn onClick={() => copy(data.sms)} testid="copy-sms" />}>
                <p className="text-slate-700 rounded bg-sky-50 px-2 py-1.5 whitespace-pre-wrap" data-testid="ai-sms">{data.sms}</p>
              </Section>
            )}
            {data.email && (
              <Section title="Suggested email" icon={<Mail size={13} className="text-amber-600" />}
                action={<CopyBtn onClick={() => copy(data.email)} testid="copy-email" />}>
                <p className="text-slate-700 rounded bg-amber-50 px-2 py-1.5 whitespace-pre-wrap" data-testid="ai-email">{data.email}</p>
              </Section>
            )}
            <p className="text-[11px] text-slate-400 italic pt-1 border-t border-slate-100">
              AI summarizes and recommends only. It does not determine eligibility or change coverage — verify details before acting.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ title, icon, action, children }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
        {icon} {title}
      </p>
      {action}
    </div>
    {children}
  </div>
);

const CopyBtn = ({ onClick, testid }) => (
  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={onClick} data-testid={testid}>
    <Copy size={12} className="mr-1" /> Copy
  </Button>
);
